import {computed, Injectable, signal} from '@angular/core';
import {decodeJwtPayload} from 'auth';

const AUTH_TOKEN_KEY = 'authToken';

@Injectable({
  providedIn: 'root'
})
export class SessionStoreDataService {
  /** Incremented whenever the auth token in sessionStorage changes, forcing recomputation. */
  private _sessionTick = signal(0);

  /**
   * Reactive signal: true when a JWT auth token is present in sessionStorage.
   * NOTE: presence alone is NOT a security guarantee — the authGuard performs
   * a live server-side validation of the token on every protected route navigation.
   */
  isLoggedInSignal = computed(() => {
    this._sessionTick(); // subscribe to changes
    return !!sessionStorage.getItem(AUTH_TOKEN_KEY);
  });

  /**
   * Reactive signal: true when the decoded token has hasActiveSubscription === true.
   */
  hasActiveSubscription = computed(() => {
    this._sessionTick();
    const decoded = this.getDecodedTokenUser();
    return decoded?.hasActiveSubscription === true;
  });

  /**
   * Reactive signal: true when the decoded token has hasActiveDigitalProducts === true.
   */
  hasActiveDigitalProducts = computed(() => {
    this._sessionTick();
    const decoded = this.getDecodedTokenUser();
    return decoded?.hasActiveDigitalProducts === true;
  });

  /**
   * Reactive signal: true when both hasActiveSubscription and hasActiveDigitalProducts are true.
   */
  hasFullAccess = computed(() => {
    return this.hasActiveSubscription() && this.hasActiveDigitalProducts();
  });

  constructor() {
    // Listen for storage events (e.g. DevTools clearing sessionStorage or another tab logging out)
    window.addEventListener('storage', (event) => {
      if (event.key === AUTH_TOKEN_KEY || event.key === null) {
        this._sessionTick.update(t => t + 1);
      }
    });
  }

  getStoredData(storageKey: string): any {
    const data = sessionStorage.getItem(storageKey);
    if (!data) return null;
    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }

  setStoredData(storageKey: string, data: any): void {
    const value = typeof data === 'string' ? data : JSON.stringify(data);
    sessionStorage.setItem(storageKey, value);
    if (storageKey === AUTH_TOKEN_KEY) {
      this._sessionTick.update(t => t + 1);
    }
  }

  clearStoredData(storageKey: string): void {
    sessionStorage.removeItem(storageKey);
    if (storageKey === AUTH_TOKEN_KEY) {
      this._sessionTick.update(t => t + 1);
    }
  }

  removeStoredData(storageKey: string): void {
    this.clearStoredData(storageKey);
  }

  /**
   * Decodes the JWT payload for UI display purposes (username, email, userGuid, etc.).
   * This is NOT a security check — the authGuard validates the token server-side.
   * Returns null if no token is present or the token is malformed.
   */
  getDecodedTokenUser(): any | null {
    // base64URL-aware decode — atob() alone throws on tokens containing '-'/'_'
    // (e.g. when a claim carries a non-ASCII character), which would otherwise make
    // a fully valid, logged-in session read as null.
    return decodeJwtPayload(sessionStorage.getItem(AUTH_TOKEN_KEY));
  }

  /** Convenience: clears the auth token and triggers the signal update. */
  logout(): void {
    this.clearStoredData(AUTH_TOKEN_KEY);
    sessionStorage.removeItem('productSelected');
  }

  /** Bumps the session tick so isLoggedInSignal recomputes (e.g. after external token store). */
  notifyTokenChange(): void {
    this._sessionTick.update(t => t + 1);
  }
}
